import {
    type ReactElement,
    type ReactNode,
    useContext,
    useMemo
} from "react";
import type ITextProps from "../components/text/type";
import {
    type LocalizeContextStateType,
    type LocalizeContextType,
    type DirectionType,
    type LocalizeType
} from "../types";
import NCoreContext, {
    type ConfigType
} from "ncore-context";
import {
    warnOnce
} from "../utils/development";
import {
    uuid
} from "../utils";
import defaultLocalesData from "../variants/locales/default.json";
import Text from "../components/text";

class NCoreUIKitLocalize<T extends LocalizeType> extends NCoreContext<LocalizeContextType, ConfigType<LocalizeContextType>> {
    onDirectionChange?: (direction: DirectionType) => void = undefined;

    projectLocales?: NCoreUIKit.Locales = undefined;

    useContext = ({
        activeLocale
    }: {
        activeLocale?: keyof NCoreUIKit.LocaleKey
    } = {}): LocalizeContextType => {
        const currentState = useContext(this.stateContext);

        if(activeLocale) {
            const activeState = this.prepare({
                activeLocale
            });

            const translations = activeState.translations ?? currentState.translations;

            return {
                ...currentState,
                ...activeState,
                localizeWithObject: (
                    translation: keyof NCoreUIKit.Translation,
                    parameters?: Array<string | ReactNode | ReactElement>,
                    props?: Array<ITextProps>
                ) => this.translateWithObject(translations, translation, parameters, props),
                localize: (
                    translation: keyof NCoreUIKit.Translation,
                    parameters?: Array<string | number>
                ) => this.translate(translations, translation, parameters),
                direction: currentState.direction,
                isRTL: currentState.isRTL
            };
        }

        return {
            ...currentState,
            localizeWithObject: this.localizeWithObject,
            localize: this.localize
        };
    };

    DirectionProvider = ({
        direction,
        children
    }: {
        direction: DirectionType;
        children?: ReactNode;
    }) => {
        const StateContextRenderer = this.stateContext;

        const currentState = useContext(this.stateContext);

        const value = useMemo(() => ({
            ...currentState,
            isRTL: direction === "rtl",
            direction
        }), [
            currentState,
            direction
        ]);

        return <StateContextRenderer.Provider
            value={value}
        >
            {children}
        </StateContextRenderer.Provider>;
    };

    constructor(initialState: T) {
        const {
            initialSelectedLocale,
            onDirectionChange,
            projectLocales
        } = initialState;

        const initialLanguage = defaultLocalesData.find(l => l.locale === "tr-TR");

        if(!initialLanguage) {
            throw new Error("Initial Locale error!.");
        }

        super({
            localizeWithObject: () => "Localization is loading...",
            direction: initialLanguage.isRTL ? "rtl" : "ltr",
            activeLocale: initialSelectedLocale ?? "tr-TR",
            localize: () => "Localization is loading...",
            translations: initialLanguage.translations,
            rruleConfig: initialLanguage.rruleConfig,
            isRTL: initialLanguage.isRTL,
            localeKeys: []
        }, {
            key: "NCoreUIKitLocalize"
        });

        this.onDirectionChange = onDirectionChange;

        this.projectLocales = projectLocales;

        const activeState = this.prepare({
            activeLocale: initialSelectedLocale ?? "tr-TR"
        });

        this.state = activeState as LocalizeContextType;
    }

    prepare = (props?: {
        activeLocale?: keyof NCoreUIKit.LocaleKey
    }): LocalizeContextStateType => {
        const locale = props && props.activeLocale ? props.activeLocale : this.state.activeLocale;

        const matchedDefaultLocale = defaultLocalesData.find(l => l.locale === locale);

        const defaultLocale = matchedDefaultLocale ?? defaultLocalesData[0]!;

        const defaultState: LocalizeContextStateType = {
            localeKeys: defaultLocalesData.map(lDI => lDI.locale) as Array<keyof NCoreUIKit.LocaleKey>,
            activeLocale: defaultLocale.locale as keyof NCoreUIKit.LocaleKey,
            direction: defaultLocale.isRTL ? "rtl" : "ltr",
            translations: defaultLocale.translations,
            rruleConfig: defaultLocale.rruleConfig,
            isRTL: defaultLocale.isRTL
        };

        const currentProjectLocale = this.projectLocales?.find(l => l.locale === locale);

        if(!matchedDefaultLocale && !currentProjectLocale) {
            warnOnce({
                message: `NCoreUIKitLocalize: locale "${locale}" is not registered (neither a built-in locale nor in projectLocales), so "${defaultLocale.locale}" is used instead.`
            });
        }

        if(!this.projectLocales || (this.projectLocales && !this.projectLocales.length)) {
            return defaultState;
        }

        if(!currentProjectLocale) {
            return defaultState;
        }

        const dLKeys = [
            ...new Set([
                ...defaultLocalesData.map(p => p.locale),
                ...this.projectLocales.map(p => p.locale)
            ])
        ];

        const baseLocale = defaultLocalesData.find(l => l.locale === locale) ?? defaultLocalesData.find(l => l.locale === "en-US") ?? defaultLocale;

        const newState: LocalizeContextStateType = {
            activeLocale: currentProjectLocale.locale as keyof NCoreUIKit.LocaleKey,
            localeKeys: dLKeys as Array<keyof NCoreUIKit.LocaleKey>,
            direction: currentProjectLocale.isRTL ? "rtl" : "ltr",
            translations: {
                ...baseLocale.translations,
                ...currentProjectLocale.translations
            },
            rruleConfig: {
                ...baseLocale.rruleConfig,
                ...currentProjectLocale.rruleConfig
            },
            isRTL: currentProjectLocale.isRTL
        };

        return newState;
    };

    translateWithObject = (
        translations: NCoreUIKit.Translation,
        translation: keyof NCoreUIKit.Translation,
        parameters?: Array<string | ReactNode | ReactElement>,
        props?: Array<ITextProps>
    ) => {
        try {
            let translatedText: string | ReactNode[] = String(translations[translation]);

            if(!translatedText) {
                return translation;
            }

            if(parameters && parameters.length) {
                parameters.forEach((_item, index) => {
                    if (typeof translatedText !== "string") return;

                    translatedText = translatedText!.split(`{{${index}}}`).flatMap((item) => [
                        item,
                        props && props[index] ? <Text
                            key={uuid()}
                            {...props[index]}
                        >
                            {parameters[index]}
                        </Text> : <Text
                            key={uuid()}
                        >
                            {parameters[index]}
                        </Text>
                    ]).slice(0, -1);
                });
            }

            return translatedText;
        } catch(e) {
            console.error(e);
            return translation;
        }
    };

    translate = (
        translations: NCoreUIKit.Translation,
        translation: keyof NCoreUIKit.Translation,
        parameters?: Array<string | number>
    ) => {
        try {
            let translatedText = translations[translation];

            if(!translatedText) {
                return translation;
            }

            if(parameters && parameters.length) {
                parameters.forEach((item, index) => {
                    translatedText = translatedText!.replace(`{{${index}}}`, String(item));
                });
            }

            return translatedText;
        } catch(e) {
            console.error(e);
            return translation;
        }
    };

    localizeWithObject = (
        translation: keyof NCoreUIKit.Translation,
        parameters?: Array<string | ReactNode | ReactElement>,
        props?: Array<ITextProps>
    ) => this.translateWithObject(this.state.translations, translation, parameters, props);

    localize = (
        translation: keyof NCoreUIKit.Translation,
        parameters?: Array<string | number>
    ) => this.translate(this.state.translations, translation, parameters);

    switch = ({
        localeKey
    }: {
        localeKey: keyof NCoreUIKit.LocaleKey
    }) => {
        const activeState = this.prepare({
            activeLocale: localeKey
        });

        this.setState(activeState);
    };

    updateProjectLocales = (newProjectLocales: NCoreUIKit.Locales, newState?: {
        activeLocale?: keyof NCoreUIKit.LocaleKey
    }) => {
        this.projectLocales = newProjectLocales;
        const activeState = this.prepare(newState);

        this.setState(activeState);
    };
}
export default NCoreUIKitLocalize;
